import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useVendorPortalServices,
  useVendorPortalDocumentService,
} from '@actschurch/ccms-sdk';
import { useVendorStore } from '../stores/vendorStore';
import { useEnrichedAuth } from './useEnrichedAuth';

/**
 * Hook return type
 */
export interface UseVendorContextReturn {
  // State
  vendorId: number | null;
  vendor: ReturnType<typeof useVendorStore>['vendor'];
  summary: ReturnType<typeof useVendorStore>['summary'];
  documentTypes: ReturnType<typeof useVendorStore>['documentTypes'];
  isLoading: boolean;
  error: string | null;
  hasVendor: boolean;
  isInitialized: boolean;

  // Actions
  fetchVendorContext: () => Promise<void>;
  refreshSummary: () => Promise<void>;
  refreshDocumentTypes: () => Promise<void>;
  clearVendorContext: () => void;

  // Computed
  canEditProfile: boolean;
  canManageProducts: boolean;
  isActive: boolean;
  isBlocked: boolean;
  statusName: string;
}

/**
 * useVendorContext Hook
 *
 * Manages vendor context including:
 * - Fetching vendor profile/summary after login
 * - Setting X-Vendor-ID header for API calls
 * - Fetching document types
 * - Handling no-vendor scenarios
 *
 * This hook should be used at the app root level to ensure
 * vendor context is available throughout the application.
 */
export function useVendorContext(): UseVendorContextReturn {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useEnrichedAuth();
  const vendorPortalServices = useVendorPortalServices();
  const documentService = useVendorPortalDocumentService();

  // Get store state and actions
  const vendorId = useVendorStore((state) => state.vendorId);
  const vendor = useVendorStore((state) => state.vendor);
  const summary = useVendorStore((state) => state.summary);
  const documentTypes = useVendorStore((state) => state.documentTypes);
  const isLoading = useVendorStore((state) => state.isLoading);
  const error = useVendorStore((state) => state.error);
  const hasVendor = useVendorStore((state) => state.hasVendor);
  const isInitialized = useVendorStore((state) => state.isInitialized);

  const needsRefetch = useVendorStore((state) => state.needsRefetch);

  const setVendorId = useVendorStore((state) => state.setVendorId);
  const setSummary = useVendorStore((state) => state.setSummary);
  const setDocumentTypes = useVendorStore((state) => state.setDocumentTypes);
  const setLoading = useVendorStore((state) => state.setLoading);
  const setSummaryLoading = useVendorStore((state) => state.setSummaryLoading);
  const setDocumentTypesLoading = useVendorStore((state) => state.setDocumentTypesLoading);
  const setError = useVendorStore((state) => state.setError);
  const setInitialized = useVendorStore((state) => state.setInitialized);
  const setNeedsRefetch = useVendorStore((state) => state.setNeedsRefetch);
  const clearVendorContext = useVendorStore((state) => state.clearVendorContext);

  // Computed helpers from store
  const canEditProfile = useVendorStore((state) => state.canEditProfile());
  const canManageProducts = useVendorStore((state) => state.canManageProducts());
  const isActive = useVendorStore((state) => state.isActive());
  const isBlocked = useVendorStore((state) => state.isBlocked());
  const statusName = useVendorStore((state) => state.getStatusName());

  // Track if we've already fetched context to prevent duplicate calls
  const hasFetchedRef = useRef(false);

  const setVendorIds = useVendorStore((state) => state.setVendorIds);

  /**
   * Resolve vendorId using fallback chain, then fetch vendor summary.
   *
   * Resolution order:
   *  1. vendorStore (persisted in localStorage/sessionStorage)
   *  2. GET /api/vendor-portal/context (lightweight, no X-Vendor-ID needed)
   *  3. GET /api/vendor-portal/profile/summary (extract from summary.vendor.vendorID)
   *  4. Navigate to /auth/vendor-register (user genuinely has no vendor)
   */
  const fetchVendorContext = useCallback(async () => {
    if (!isAuthenticated) {
      clearVendorContext();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Step 1: Check if vendorId is already in the store (from login response or persisted storage)
      let resolvedVendorId = useVendorStore.getState().vendorId;

      // Step 2: If not in store, try the lightweight context endpoint
      if (!resolvedVendorId) {
        console.log('[useVendorContext] No vendorId in store. Trying /vendor-portal/context endpoint...');
        try {
          const context = await vendorPortalServices.profile.getVendorContext();
          if (context.primaryVendorId) {
            resolvedVendorId = context.primaryVendorId;
            setVendorId(resolvedVendorId);
            setVendorIds(context.vendorIds);
            vendorPortalServices.setVendorId(resolvedVendorId);
            console.log('[useVendorContext] Resolved vendorId from context endpoint:', resolvedVendorId);
          } else if (context.vendorIds.length === 0) {
            // User genuinely has no vendors
            console.warn('[useVendorContext] User has no vendors. Redirecting to registration.');
            setError('No vendor found for this user');
            setInitialized(true);
            navigate('/auth/vendor-register');
            return;
          }
        } catch (contextErr: any) {
          console.warn('[useVendorContext] Context endpoint failed, falling back to getSummary:', contextErr?.message);
          // Fall through to step 3
        }
      } else {
        // vendorId already in store — ensure it's set on services
        vendorPortalServices.setVendorId(resolvedVendorId);
      }

      // Step 3: Fetch full vendor summary (with or without vendorId — backend has UserVendors fallback)
      const vendorSummary = await vendorPortalServices.profile.getSummary();

      // If we still didn't have vendorId, extract from summary
      if (!resolvedVendorId && vendorSummary.vendor?.vendorID) {
        resolvedVendorId = vendorSummary.vendor.vendorID;
        vendorPortalServices.setVendorId(resolvedVendorId);
        console.log('[useVendorContext] Resolved vendorId from summary:', resolvedVendorId);
      }

      // Update store with summary (this also sets vendorId in the store)
      setSummary(vendorSummary);
      setInitialized(true);

      // Fetch document types in parallel
      fetchDocumentTypes();
    } catch (err: any) {
      console.error('[useVendorContext] Error fetching vendor context:', err);

      // Handle no vendor case (404)
      if (err?.status === 404 || err?.response?.status === 404) {
        console.warn('[useVendorContext] 404 - No vendor found for this user.');
        setError('No vendor found for this user');
        setInitialized(true);
        navigate('/auth/vendor-register');
        return;
      }

      // Handle forbidden (403) - user not linked to any vendor
      if (err?.status === 403 || err?.response?.status === 403) {
        console.warn('[useVendorContext] 403 - Access denied. User is not linked to any vendor.');
        setError('No vendor access. Please register a vendor.');
        setInitialized(true);
        navigate('/auth/vendor-register');
        return;
      }

      setError(err?.message || 'Failed to fetch vendor context');
      setInitialized(true);
    } finally {
      setLoading(false);
    }
  }, [
    isAuthenticated,
    vendorPortalServices,
    navigate,
    clearVendorContext,
    setLoading,
    setError,
    setSummary,
    setInitialized,
    setVendorId,
    setVendorIds,
  ]);

  /**
   * Fetch document types
   */
  const fetchDocumentTypes = useCallback(async () => {
    setDocumentTypesLoading(true);

    try {
      const types = await documentService.getDocumentTypes();
      setDocumentTypes(types);
    } catch (err: any) {
      console.error('[useVendorContext] Error fetching document types:', err);
      // Don't set error for document types - not critical
    } finally {
      setDocumentTypesLoading(false);
    }
  }, [documentService, setDocumentTypes, setDocumentTypesLoading]);

  /**
   * Refresh summary only
   */
  const refreshSummary = useCallback(async () => {
    if (!vendorId) return;

    setSummaryLoading(true);
    setError(null);

    try {
      const vendorSummary = await vendorPortalServices.profile.getSummary();
      setSummary(vendorSummary);
    } catch (err: any) {
      console.error('[useVendorContext] Error refreshing summary:', err);
      setError(err?.message || 'Failed to refresh vendor summary');
    } finally {
      setSummaryLoading(false);
    }
  }, [vendorId, vendorPortalServices, setSummary, setSummaryLoading, setError]);

  /**
   * Refresh document types only
   */
  const refreshDocumentTypes = useCallback(async () => {
    if (!vendorId) return;
    await fetchDocumentTypes();
  }, [vendorId, fetchDocumentTypes]);

  /**
   * Auto-fetch vendor context when user logs in or after vendor registration
   */
  useEffect(() => {
    // Force refetch if needsRefetch flag is set (e.g., after vendor registration)
    if (isAuthenticated && needsRefetch && !isLoading) {
      hasFetchedRef.current = true;
      setNeedsRefetch(false);
      fetchVendorContext();
      return;
    }

    // Normal fetch if:
    // 1. User is authenticated
    // 2. We haven't already fetched
    // 3. We're not currently loading
    // 4. Context is not already initialized
    if (isAuthenticated && !hasFetchedRef.current && !isLoading && !isInitialized) {
      hasFetchedRef.current = true;
      fetchVendorContext();
      return;
    }

    // RECOVERY: If authenticated and initialized but vendorId is missing,
    // the persisted storage was likely cleared mid-session. Re-fetch from backend.
    // The backend's getSummary() works without X-Vendor-ID header — it falls back
    // to the user's primary vendor via the UserVendors table.
    // The !error guard prevents infinite retry when the user genuinely has no vendor.
    if (isAuthenticated && isInitialized && !vendorId && !isLoading && !error) {
      console.warn('[useVendorContext] vendorId lost (storage cleared?). Re-fetching vendor context.');
      fetchVendorContext();
      return;
    }

    // Reset fetch flag when user logs out
    if (!isAuthenticated) {
      hasFetchedRef.current = false;
      clearVendorContext();
    }
  }, [isAuthenticated, isLoading, isInitialized, needsRefetch, vendorId, error, fetchVendorContext, clearVendorContext, setNeedsRefetch]);

  /**
   * Update service vendor IDs when vendorId changes
   */
  useEffect(() => {
    if (vendorId) {
      vendorPortalServices.setVendorId(vendorId);
    }
  }, [vendorId, vendorPortalServices]);

  /**
   * Listen for storage changes from other tabs.
   * If vendor-storage is cleared in another tab, re-fetch vendor context.
   * Note: The 'storage' event only fires in OTHER tabs, not the originating tab.
   * Same-tab clearing is handled by the recovery condition in the auto-fetch effect above.
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'vendor-storage' && event.newValue === null && isAuthenticated) {
        console.warn('[useVendorContext] vendor-storage cleared in another tab. Re-fetching.');
        fetchVendorContext();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [isAuthenticated, fetchVendorContext]);

  return {
    // State
    vendorId,
    vendor,
    summary,
    documentTypes,
    isLoading,
    error,
    hasVendor,
    isInitialized,

    // Actions
    fetchVendorContext,
    refreshSummary,
    refreshDocumentTypes,
    clearVendorContext,

    // Computed
    canEditProfile,
    canManageProducts,
    isActive,
    isBlocked,
    statusName,
  };
}

/**
 * Alias for consistency with other hooks
 */
export const useVendor = useVendorContext;
